"use client";
import { motion } from "framer-motion";
import { Shield, ArrowLeft, Eye, Lock, Database, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-background">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="container mx-auto px-4 py-8"
      >
        <div className="flex items-center justify-between mb-8">
          <Link href="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>

        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6"
          >
            <Shield className="h-8 w-8 text-primary" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold text-foreground mb-4"
          >
            Privacy Policy
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-muted-foreground text-lg max-w-2xl mx-auto"
          >
            How we collect, use, and protect your information
          </motion.p>
        </div>
      </motion.div>

      {/* Content */}
      <div className="container mx-auto px-4 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="max-w-4xl mx-auto"
        >
          <div className="p-8 rounded-2xl bg-card/50 backdrop-blur-sm border border-white/[0.1] space-y-8">
            
            {/* Information Collection */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Database className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground">Information We Collect</h2>
              </div>
              <div className="space-y-4 text-muted-foreground ml-13">
                <p>
                  <strong className="text-foreground">Personal Information:</strong> When you create an account, we collect your name, email address, and authentication details (including Google OAuth if you choose to sign in with Google).
                </p>
                <p>
                  <strong className="text-foreground">Assignment Data:</strong> We process and store the assignments you upload for scoring, including document content, rubrics, and grading results.
                </p>
                <p>
                  <strong className="text-foreground">Usage Information:</strong> We collect information about how you interact with our service, including access times, pages viewed, and features used.
                </p>
              </div>
            </section>

            {/* How We Use Information */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Eye className="h-5 w-5 text-accent" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground">How We Use Your Information</h2>
              </div>
              <div className="space-y-4 text-muted-foreground ml-13">
                <ul className="space-y-2 list-disc list-inside">
                  <li>To provide and maintain our AI-powered assignment scoring service</li>
                  <li>To process your assignments and generate detailed feedback and rubric analysis</li>
                  <li>To improve our AI models and scoring algorithms</li>
                  <li>To communicate with you about service updates and support</li>
                  <li>To ensure the security and integrity of our platform</li>
                  <li>To comply with legal obligations and protect our rights</li>
                </ul>
              </div>
            </section>

            {/* Data Security */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Lock className="h-5 w-5 text-green-500" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground">Data Security</h2>
              </div>
              <div className="space-y-4 text-muted-foreground ml-13">
                <p>
                  We implement industry-standard security measures to protect your personal information and assignment data:
                </p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Encrypted data transmission using HTTPS/SSL</li>
                  <li>Secure authentication with HTTP-only cookies</li>
                  <li>Regular security audits and updates</li>
                  <li>Restricted access to personal data on a need-to-know basis</li>
                  <li>Secure cloud storage with industry-leading providers</li>
                </ul>
              </div>
            </section>

            {/* Your Rights */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <UserCheck className="h-5 w-5 text-blue-500" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground">Your Rights</h2>
              </div>
              <div className="space-y-4 text-muted-foreground ml-13">
                <p>You have the right to:</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Access and review your personal information</li>
                  <li>Request corrections to your data</li>
                  <li>Delete your account and associated data</li>
                  <li>Export your assignment history and scores</li>
                  <li>Opt-out of promotional communications</li>
                  <li>Withdraw consent for data processing (where applicable)</li>
                </ul>
              </div>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Data Retention</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  We retain your personal information and assignment data for as long as your account is active or as needed to provide you services. If you request account deletion, we will delete or anonymize your information within 30 days, except where we are required to retain it for legal or legitimate business purposes.
                </p>
              </div>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Third-Party Services</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  We use third-party services to help provide our platform:
                </p>
                <ul className="space-y-2 list-disc list-inside">
                  <li><strong className="text-foreground">Google OAuth:</strong> For secure authentication (subject to Google's privacy policy)</li>
                  <li><strong className="text-foreground">AI Service Providers:</strong> For processing and scoring assignments (under strict data processing agreements)</li>
                  <li><strong className="text-foreground">Cloud Infrastructure:</strong> For hosting and data storage with enterprise-grade security</li>
                </ul>
              </div>
            </section>

            {/* Updates to Policy */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Updates to This Policy</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any significant changes by email or through a prominent notice on our platform. Your continued use of AssignOwl after changes are posted constitutes acceptance of the updated policy.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section className="pt-6 border-t border-white/[0.1]">
              <h2 className="text-2xl font-semibold text-foreground mb-4">Contact Us</h2>
              <div className="space-y-2 text-muted-foreground">
                <p>If you have questions about this Privacy Policy, please contact us:</p>
                <p className="text-foreground">
                  <strong>Email:</strong> privacy@assignowl.com<br />
                  <strong>Company:</strong> Greenguide Solutions Pvt Limited
                </p>
              </div>
            </section>

            <div className="text-sm text-muted-foreground text-center pt-6 border-t border-white/[0.1]">
              Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
